import { useEffect, useState } from 'react';
import {
    collectImageFileIds,
    extractPayloadFromEvent,
    rewriteImageSrcs
} from '../../../patient-files/actions/dx-report/editor/payloadHelpers';
import { resolveInternalImageUrls } from '../../../../services/internalImages';
import { reportError } from '../../../../services/errorReporting';
import type { DxReportPayload } from '../../../patient-files/actions/dx-report/types';
import type { TimelineEvent } from '../../../patient-files/actions/types';

// The draft's payload with its images re-signed, so a read-only render shows them.
export function useResolvedDxPayload(patientId: string, draft: TimelineEvent | null) {
    const [payload, setPayload] = useState<DxReportPayload | null>(null);
    const [isResolving, setIsResolving] = useState(true);

    useEffect(() => {
        let cancelled = false;
        const base = draft ? extractPayloadFromEvent(draft) : null;
        if (!base) {
            setPayload(null);
            setIsResolving(false);
            return;
        }
        const fileIds = collectImageFileIds(base);
        if (fileIds.length === 0) {
            setPayload(base);
            setIsResolving(false);
            return;
        }
        setIsResolving(true);
        resolveInternalImageUrls(patientId, fileIds)
            .then((urls) => {
                if (!cancelled) setPayload(rewriteImageSrcs(base, urls));
            })
            .catch((err) => {
                // Fall back to the unresolved payload (images may be broken), but report it.
                reportError({
                    source: 'DxDraftPreview.resolveImageUrls',
                    message: 'Failed to resolve Dx draft image URLs',
                    stack: err instanceof Error ? err.stack : undefined,
                    url: globalThis.location?.href ?? '',
                    userAgent: navigator.userAgent,
                    errorType: 'unhandledrejection',
                    metadata: { patientId, fileCount: fileIds.length }
                });
                if (!cancelled) setPayload(base);
            })
            .finally(() => {
                if (!cancelled) setIsResolving(false);
            });
        return () => {
            cancelled = true;
        };
    }, [patientId, draft]);

    return { payload, isResolving };
}
